// Data hooks for Gallery theming. Reads ride the SAME react-query profile cache
// the Gallery page already populates (useGetUserProfileById, keyed by
// [QUERY_KEYS.useGetUserProfileById, userId]) — no duplicate fetch. Writes go
// through the existing update-profile mutation and invalidate that key so every
// consumer re-renders from server truth.
//
// RESILIENCE (BE gap THM-F1/THM-F2): the backend UserProfile serializer may not
// yet accept `gallery_theme`. Every save persists to localStorage FIRST so the
// creator never loses their theme, THEN attempts the PATCH. If the server drops
// or rejects the field, the local copy keeps the studio + this browser's render
// working; a soft toast tells the user persistence is pending backend support.
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useQueryClient } from "react-query";
import { useUserUpdateProfile } from "../../services/mutations";
import { editUserProfileAction } from "../../redux/actions/user";
import { QUERY_KEYS } from "../../utils/config";
import { normalizeGalleryTheme } from "./galleryTheme";

const LS_PREFIX = "iy::gallery_theme::";
const lsKey = (userId) => `${LS_PREFIX}${userId || "me"}`;

export const readLocalGalleryTheme = (userId) => {
  try {
    const raw = localStorage.getItem(lsKey(userId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const writeLocalGalleryTheme = (userId, theme) => {
  try {
    localStorage.setItem(lsKey(userId), JSON.stringify(theme || {}));
  } catch {
    /* storage full / unavailable — non-fatal, in-memory draft still applies */
  }
};

// Resolve the theme to render for a given profile. Server value wins; for the
// OWNER we fall back to the local copy (covers the BE-gap window where the
// server can't yet echo the saved field). A viewer of someone else's gallery
// only ever sees the server value (never another user's localStorage).
export const resolveProfileGalleryTheme = (profile, userId, isOwn) => {
  const server = profile?.gallery_theme;
  const local = isOwn ? readLocalGalleryTheme(userId) : null;
  return normalizeGalleryTheme(server || local || {});
};

// Mutation hook. `save(theme, { onSuccess, onError, onLocalOnly })`.
export const useSaveGalleryTheme = (userId) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const updateProfile = useUserUpdateProfile();

  const save = useCallback(
    (theme, { onSuccess, onError, onLocalOnly } = {}) => {
      const safeTheme = normalizeGalleryTheme(theme);
      // 1) optimistic local persist — the theme survives even if the BE drops it.
      writeLocalGalleryTheme(userId, safeTheme);
      // 1b) BUG FIX (ISP-9999, gordon-0913-gallery-theme-save-does-not-apply
      // follow-up): on the OWNER'S OWN /gallery/:id, galleryLoadState.js's
      // shouldSkipOwnProfileFetch intentionally disables this exact
      // useGetUserProfileById query (perf fix cbx-gallery-firstpaint-slower-
      // gecko-webkit) and seeds it once from redux `state.userProfile.userProfile`
      // instead. react-query never auto-refetches a disabled query on
      // invalidateQueries, so the sibling fix below (omitting the id segment)
      // never reaches an already-mounted own-gallery page — MEASURED live on
      // web-dev.iyuga.net: PATCH round-trips fine (server echoes the new
      // accent), but the open Gallery page's `--gt-accent` stays pinned to the
      // pre-save value until a hard reload. Patching redux directly (the same
      // action editUserProfileAction/EDIT_USER_PROFILE_SUCCESS already used
      // for other own-profile edits) reaches that shortcut path too.
      dispatch(editUserProfileAction({ gallery_theme: safeTheme }));

      // 2) attempt server persistence via the existing update-profile seam.
      updateProfile.mutate(
        { user_profile: { gallery_theme: safeTheme } },
        {
          onSuccess: (res) => {
            const echoed = res?.data?.gallery_theme;
            // Invalidate so the Gallery + studio re-read server truth.
            //
            // BUG FIX (ISP-9999, gordon-0913-gallery-theme-save-does-not-apply):
            // pages/Gallery/index.jsx mounts `useGetUserProfileById(id)` with
            // `id` from `useParams()`, which is ALWAYS A STRING. Every entry
            // point into this Studio (ThemesPopup, GallerySettings, the
            // standalone GalleryTheme page) opens it with a NUMERIC userId
            // (getLoggedInUser()?.id / userProfile?.id). react-query v3
            // matches invalidateQueries keys by per-segment deep-equality, so
            // `[KEY, 12675]` never matched the live `[KEY, "12675"]` query and
            // the real Gallery silently kept rendering the pre-save theme
            // (the Studio's own preview looked fine because it renders from
            // local `draft` state, not this cache). Omit the id segment so
            // every query under this key gets invalidated regardless of how
            // its id was typed -- the same pattern already used for this
            // exact class of bug in ProfileBox/index.jsx (EW-661).
            queryClient.invalidateQueries([QUERY_KEYS.useGetUserProfileById]);
            if (echoed === undefined) {
              // Server accepted the PATCH but silently ignored the unknown field
              // (BE gap): local copy is the source of truth for now.
              onLocalOnly && onLocalOnly(safeTheme);
            }
            onSuccess && onSuccess(res, safeTheme);
          },
          onError: (err) => {
            // Server rejected the field/request. Local persist already succeeded.
            if (onError) onError(err, safeTheme);
            else onLocalOnly && onLocalOnly(safeTheme);
          },
        }
      );
    },
    [userId, queryClient, dispatch, updateProfile]
  );

  return { save, isSaving: updateProfile.isLoading };
};
